import { useEffect, useState } from 'react'
import { customerData, LockboxAddressInfo, LockboxBankInfo, LockboxDLInfo, LockBoxInfo, LockboxSpouseInfo } from '../../../utilities/CustomerData'
import { Autocomplete, useLoadScript } from '@react-google-maps/api'
import { useMask } from '@react-input/mask'
import { apiURL } from '../../../utilities/URLs'
import { refreshAccessToken } from '../../../utilities/extensions'
import { useNavigate } from 'react-router-dom'
import BlueInkEmbed from '@blueink360/blueink-embed-js'

export default function RefundLockBox({ processingFee, masterStep, setShowLockBox, setMasterStep }) {
  const navigate = useNavigate()

  const [step, setStep] = useState(0)
  const [bankInfo, setBankInfo] = useState(new LockboxBankInfo())
  const [dlInfo, setDLInfo] = useState(new LockboxDLInfo())
  const [addressInfo, setAddressInfo] = useState(new LockboxAddressInfo())
  const [spouseInfo, setSpouseInfo] = useState(new LockboxSpouseInfo())
  const [autocomplete, setAutocomplete] = useState(null)
  const [submittingInfo, setSubmittingInfo] = useState(false)
  const [embedUrl, setEmbedUrl] = useState('')
  const [gettingEmbedURL, setGettingEmbedURL] = useState(true)
  const [agree, setAgree] = useState(false)
  const [error, setError] = useState(false)

  const embed = new BlueInkEmbed(process.env.REACT_APP_BLUEINK_PUBLIC_API_KEY)
  
  embed.on(BlueInkEmbed.EVENT.COMPLETE, async () => {
    await handleSubmitLockboxInfo()
  })

  const libraries = ['places']
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries
  })

  const handleLoad = (autocompleteInstance) => {
    setAutocomplete(autocompleteInstance)
  }

  const handlePlaceChanged = () => {
    const getAddressComponent = (type, useShortName = false) => {
      const component = place.address_components.find(comp => comp.types.includes(type))
      return component ? (useShortName ? component.short_name : component.long_name) : ''
    }

    const place = autocomplete.getPlace()
    if (place) {
      if(step === 2) {
        setAddressInfo((prevAddress) => ({
          street: `${getAddressComponent('street_number')} ${getAddressComponent('route')}`.trim(),
          city: getAddressComponent('locality'),
          state: getAddressComponent('administrative_area_level_1', true),
          zip: getAddressComponent('postal_code'),
        }))
      } else {
        setDLInfo((prevDL) => ({
          ...prevDL,
          issuePlace: getAddressComponent('administrative_area_level_1')
        }))
      }
    }
  }

  const handleNext = () => setStep((prev) => prev + 1)
  const handleBack = () => setStep((prev) => prev - 1)

  const spouseSSNRef = useMask({
    mask: '___-__-____',
    replacement: { _: /\d/ }
  })

  const handleSubmitLockboxInfo = async () => {
    setSubmittingInfo(true)

    const form = new FormData()
    form.append('lockBoxInfo', JSON.stringify({
      leadId: customerData.customerLeadData.leadId,
      customerId: customerData.customerLeadData.leadContact.contactId,
      processingFee: processingFee,
      spouseInfo: spouseInfo,
      dlInfo: dlInfo,
      bankInfo: bankInfo,
      addressInfo: addressInfo
    }))

    const lockBoxResponse = await fetch(`${apiURL}/api/submitLockBoxInfo`, {
      method: 'POST',
      headers: {
        'Authorization': customerData.accessToken
      },
      body: form
    })
    const lockBoxData = await lockBoxResponse.json()
    setSubmittingInfo(false)
    
    if(lockBoxData.response.error) {
      if(lockBoxData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleSubmitLockboxInfo() : navigate('/', { state: { expired: true } })
      } else if(lockBoxData.response.error === 'No IDme Attributes.')
        navigate('/resyncIDme')
      else {
        embed.unmount('#iframe-container') 
        setStep(1)
        setError(true)
        console.error(lockBoxData.response.error)
      }
    } else {
      customerData.updateCustomerStage('01ac55ea-c71f-44df-8b23-3e64685ca01f', 'de9f6d4a-0aff-4b68-9a87-7a42ae371a61', 'Documents Available - Postpaid Lockbox')
      customerData.customerLockBoxInfo = new LockBoxInfo(lockBoxData.response.lockBoxInfo)
      setShowLockBox(false)
    }
  }

  useEffect(() => {
    const handleGetLockboxEmbedURL = async () => {
      const form = new FormData()
      form.append('embedInfo', JSON.stringify({
        leadContact: customerData.customerLeadData.leadContact,
        lockBoxBankInfo: bankInfo,
        isMarriedFilingJoint: customerData.customerDocuments.marriedFilingJoint,
        spouseName: spouseInfo.firstName+' '+spouseInfo.lastName
      }))

      const embedUrlResponse = await fetch(`${apiURL}/api/getLockboxEmbedURL`, {
        method: 'POST',
        headers: {
          'Authorization': customerData.accessToken
        },
        body: form
      })
      const embedUrlData = await embedUrlResponse.json()
      setGettingEmbedURL(false)

      if(embedUrlData.response.error) {
        if(embedUrlData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? await handleGetLockboxEmbedURL() : navigate('/', { state: { expired: true } })
        } else if(embedUrlData.response.error === 'No IDme Attributes.')
            navigate('/resyncIDme') 
          else if(embedUrlData.response.error === 'Expired Documents')
            navigate('/syncNewDocuments', { state: { upload: true }})
          else 
            console.error(embedUrlData.response.error)
      } else
        setEmbedUrl(embedUrlData.response.embedUrl)
    }
    if(step === 5) 
      handleGetLockboxEmbedURL()
  }, [step])

  useEffect(() => {
    if(embedUrl)
      embed.mount(embedUrl, '#iframe-container', { height: '1000px' })
  }, [embedUrl])

  return isLoaded ? (
    <div className='w-screen flex flex-col items-center gap-6 p-4'>
      <p className='text-lg text-center'>Before we finalize your documents, let's make sure we can get your refund to you.</p>
      {step === 0 && (
        <div className='w-full flex flex-col items-center'>
          <div className='lg:max-w-screen-lg w-11/12 flex flex-col items-center gap-2'>
            <p className='text-2xl font-bold'>Post-Paid Payment Fees and Disbursements</p>
            <p>This refund will be paid out to you in the details and fees outlined below.</p>
            <div className='w-full flex flex-col'>
              <p className='font-bold'>Payable to you:</p>
              <div className='flex lg:flex-row flex-col gap-4 items-stretch'>
                <div className='flex flex-col flex-1 p-4 rounded-md shadow-md bg-gray-50'>
                  <div className='flex flex-row justify-between'>
                    <p className='font-bold'>Payout</p>
                    <p className='font-bold p-2 rounded-full bg-gray-100 text-xs'>12-15 weeks</p>
                  </div>
                  <p>Paid to you in about 12-15 weeks.</p>
                  <div className='flex flex-row items-center justify-between rounded-lg bg-gray-100 p-2 mt-auto'>
                    <p className='font-bold'>${(customerData.customerLeadData.leadAmount - processingFee - 44.95).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className='w-full flex flex-col gap-2'>
              <div onClick={() => setAgree(!agree)} className='bg-gray-100 rounded-lg p-4 flex flex-row gap-2 cursor-pointer'>
                <input
                  type='checkbox'
                  id='checkbox1'
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                  className='mr-2'
                />
                <p>I agree to the pricing and fee schedule for my refund</p>
              </div>
              <div className='w-full flex flex-row items-center gap-2'>
                <button onClick={() => setMasterStep(masterStep - 3)} className='w-1/2 bg-gray-200 rounded-lg p-2'>
                  Back
                </button>
                <button disabled={!agree} onClick={() => setStep(step + 1)} className={`w-1/2 rounded-lg p-2 text-white ${agree ? 'bg-primaryColor hover:bg-primaryDarkColor transition duration-200' : 'bg-gray-400 cursor-not-allowed'}`}>
                  Continue
                </button>
              </div>
            </div>
            <div className='w-full border-b-2 border-gray-200 m-8'></div>
            <div className='w-full flex flex-col gap-20'>
              <div className='w-full flex flex-col'>
                <p className='font-bold text-gray-500'>Payment Breakdown</p>
                <div className='overflow-x-auto'>
                  <table className='w-full border-collapse border border-gray-300 mt-4'>
                    <thead>
                      <tr className='bg-gray-100'>
                        <th className='border border-gray-300 px-4 py-2 text-left'>Fee</th>
                        <th className='border border-gray-300 px-4 py-2 text-left'>Amount</th>
                        <th className='border border-gray-300 px-4 py-2 text-left'>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className='border border-gray-300 px-4 py-2'>Initial Tax Credit Amount</td>
                        <td className='border border-gray-300 px-4 py-2 text-green-500 text-nowrap text-center'>${customerData.customerLeadData.leadAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className='border border-gray-300 px-4 py-2'>Your current calculated tax credit</td>
                      </tr>
                      <tr>
                        <td className='border border-gray-300 px-4 py-2'>Processing Fee</td>
                        <td className='border border-gray-300 px-4 py-2 text-red-500 text-nowrap text-center'>-${(Number(processingFee)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className='border border-gray-300 px-4 py-2'>Paid from your refund amount from the IRS.</td>
                      </tr>
                      <tr>
                        <td className='border border-gray-300 px-4 py-2'>Fraud, Compliance, and Verification Fee (3rd Party)</td>
                        <td className='border border-gray-300 px-4 py-2 text-red-500 text-nowrap text-center'>-$44.95</td>
                        <td className='border border-gray-300 px-4 py-2'>Fee paid to Evolve Bank & Trust from your refund from the IRS.</td>
                      </tr>
                      <tr>
                        <td className='border border-gray-300 px-4 py-2'>Total disbursed to you within 12 to 15 weeks</td>
                        <td className='border border-gray-300 px-8 py-2 text-green-500 text-nowrap'>+${(customerData.customerLeadData.leadAmount - processingFee - 44.95).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className='border border-gray-300 px-4 py-2'>These funds will be depositied into your bank account after the IRS has issued your refund.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {step === 1 && (
        <div className='w-11/12 max-w-3xl bg-white p-4 shadow-md rounded-lg border'>
          <p className='font-bold text-xl'>Banking Information</p>
          <p className='text-gray-600 mb-4'>Where do you want your refund?</p>
          <div className='mb-4'>
            <label className='block font-bold text-gray-700 mb-1'>Bank Institution*</label>
            <input
              type='text'
              className='w-full px-4 py-2 border rounded-lg'
              value={bankInfo.name}
              onChange={(e) => setBankInfo({ ...bankInfo, name: e.target.value })}
              required
            />
          </div>
          <div className='mb-4'>
            <label className='block font-bold text-gray-700 mb-1'>Select Bank Type</label>
            <select
              value={bankInfo.type}
              onChange={(e) => setBankInfo({ ...bankInfo, type: e.target.value })}
              className='w-full border rounded-lg px-4 py-2'
            >
              <option value='' disabled>Select Account Type</option>
              <option value='C'>CHECKING</option>
              <option value='S'>SAVINGS</option>
            </select>
          </div>
          <div className='mb-4'>
            <label className='block font-bold text-gray-700 mb-1'>Account Number*</label>
            <input
              type='text'
              className='w-full px-4 py-2 border rounded-lg'
              value={bankInfo.dan}
              onChange={(e) => setBankInfo({ ...bankInfo, dan: e.target.value.trim() })}
              required
            />
          </div>
          <div>
            <label className='block font-bold text-gray-700 mb-1'>Routing Number*</label>
            <input
              type='text'
              className='w-full px-4 py-2 border rounded-lg'
              value={bankInfo.rtn}
              maxLength={9}
              onChange={(e) => setBankInfo({ ...bankInfo, rtn: e.target.value.trim() })}
              required
            />
          </div>
          {error && (
            <p className='text-red-600 text-sm mt-4'>There seems to have been an error submitting your information. Please double check all information is correct.</p>
          )}
          <div className='w-full flex flex-col items-end mt-4'>
            <button disabled={!bankInfo.type || !bankInfo.dan || !bankInfo.rtn || bankInfo.rtn.length !== 9} onClick={handleNext} className={`px-4 py-2 text-white rounded-lg ${!bankInfo.type || !bankInfo.dan || !bankInfo.rtn || bankInfo.rtn.length !== 9 ? 'bg-gray-400 cursor-not-allowed' : 'bg-primaryColor hover:bg-primaryDarkColor duration-200'}`}>
              Next
            </button>
          </div>
        </div>
      )}
      {step === 2 && (
        <div className='w-11/12 max-w-3xl bg-white p-4 shadow-md rounded-lg border'>
          <p className='font-bold text-xl'>Address Information</p>
          <p className='text-gray-600 mb-4'>Please Confirm Your Address.</p>
          <div className='mb-4'>
            <label className='block font-bold text-gray-700 mb-1'>Street Address*</label>
            <Autocomplete onLoad={handleLoad} onPlaceChanged={handlePlaceChanged}>
              <input
                type='text'
                className='w-full px-4 py-2 border rounded-lg'
                value={addressInfo.street || ''}
                onChange={(e) => setAddressInfo({ ...addressInfo, street: e.target.value })}
                required
              />
            </Autocomplete>
          </div>
          <div className='mb-4'>
            <label className='block font-bold text-gray-700 mb-1'>City*</label>
            <input
              type='text'
              className='w-full px-4 py-2 border rounded-lg'
              value={addressInfo.city || ''}
              onChange={(e) => setAddressInfo({ ...addressInfo, city: e.target.value })}
              required
            />
          </div>
          <div className='mb-4'>
            <label className='block font-bold text-gray-700 mb-1'>State*</label>
            <input
              type='text'
              className='w-full px-4 py-2 border rounded-lg'
              value={addressInfo.state || ''}
              onChange={(e) => setAddressInfo({ ...addressInfo, state: e.target.value })}
              required
            />
          </div>
          <div className='mb-4'>
            <label className='block font-bold text-gray-700 mb-1'>ZIP Code*</label>
            <input
              type='text'
              className='w-full px-4 py-2 border rounded-lg'
              value={addressInfo.zip || ''}
              onChange={(e) => setAddressInfo({ ...addressInfo, zip: e.target.value })}
              required
            />
          </div>
          <div className='w-full flex flex-row items-center justify-between mt-4'>
            <button onClick={handleBack} className='px-4 py-2 text-white rounded-lg bg-secondaryColor hover:bg-secondaryDarkColor duration-200'>
              Back
            </button>
            <button disabled={!addressInfo.street || !addressInfo.city || !addressInfo.state || !addressInfo.zip} onClick={handleNext} className={`px-4 py-2 text-white rounded-lg ${!addressInfo.street || !addressInfo.city || !addressInfo.state || !addressInfo.zip ? 'bg-gray-400 cursor-not-allowed' : 'bg-primaryColor hover:bg-primaryDarkColor duration-200'}`}>
              Next
            </button>
          </div>
        </div>
      )}
      {step === 3 && (
        <div className='w-11/12 max-w-3xl bg-white p-4 shadow-md rounded-lg border'>
          <p className='font-bold text-xl'>Identification Information</p>
          <p className='text-gray-600 mb-4'>Please Confirm Your Identity.</p>
          <div className='mb-4'>
            <label className='block font-bold text-gray-700 mb-1'>Select Identification Type</label>
            <select
              value={dlInfo.type}
              onChange={(e) => setDLInfo({ ...dlInfo, type: e.target.value })}
              className='w-full border rounded-lg px-4 py-2'
            >
              <option value='' disabled>Select Identification Type</option>
              <option value='C'>Driver's License</option>
              <option value='E'>US Passport</option>
              <option value='F'>Foreign Passport</option>
            </select>
          </div>
          <div className='mb-4'>
            <label className='block font-bold text-gray-700 mb-1'>Identification Number*</label>
            <input
              type='text'
              className='w-full px-4 py-2 border rounded-lg'
              value={dlInfo.number}
              onChange={(e) => setDLInfo({ ...dlInfo, number: e.target.value.trim() })}
              required
            />
          </div>
          <div className='mb-4'>
            <label className='block font-bold text-gray-700 mb-1'>Issue State*</label>
            <Autocomplete onLoad={handleLoad} onPlaceChanged={handlePlaceChanged}>
              <input
                type='text'
                className='w-full px-4 py-2 border rounded-lg'
                value={dlInfo.issuePlace}
                onChange={(e) => setDLInfo({ ...dlInfo, issuePlace: e.target.value })}
              />
            </Autocomplete>
          </div>
          <div className='mb-4'>
            <label className='block font-bold text-gray-700 mb-1'>Issue Date*</label>
            <input
              type='date'
              className='w-full px-4 py-2 border rounded-lg'
              value={dlInfo.issueDate}
              onChange={(e) => setDLInfo({ ...dlInfo, issueDate: e.target.value })}
            />
          </div>
          <div className='mb-4'>
            <label className='block font-bold text-gray-700 mb-1'>Expiration Date*</label>
            <input
              type='date'
              className='w-full px-4 py-2 border rounded-lg'
              value={dlInfo.expirationDate}
              onChange={(e) => setDLInfo({ ...dlInfo, expirationDate: e.target.value })}
            />
          </div>
          <div className='w-full flex flex-row items-center justify-between mt-4'>
            <button disabled={submittingInfo} onClick={handleBack} className={`px-4 py-2 text-white rounded-lg ${submittingInfo ? 'bg-gray-400 cursor-not-allowed' : 'bg-secondaryColor hover:bg-secondaryDarkColor duration-200'}`}>
              Back
            </button>
            <button disabled={!dlInfo.type || !dlInfo.number || !dlInfo.issuePlace || !dlInfo.expirationDate || !dlInfo.issueDate || submittingInfo} onClick={() => customerData.customerDocuments.marriedFilingJoint ? handleNext() : setStep(5)} className={`px-4 py-2 text-white rounded-lg ${!dlInfo.type || !dlInfo.number || !dlInfo.issuePlace || !dlInfo.expirationDate || !dlInfo.issueDate || submittingInfo ? 'bg-gray-400 cursor-not-allowed' : 'bg-primaryColor hover:bg-primaryDarkColor duration-200'}`}>
              Next
            </button>
          </div>
          {submittingInfo && (
            <div className='flex flex-row items-center justify-center gap-2'>
              <div className='loader ease-linear rounded-full border-[6px] border-t-[6px]  h-8 w-8 mt-4 mb-4'></div>
              <p>Sending Information...</p>
            </div>
          )}
        </div>
      )}
      {step === 4 && (
        <div className='w-11/12 max-w-3xl bg-white p-4 shadow-md rounded-lg border'>
          <p className='font-bold text-xl'>Spouse Information</p>
          <p className='text-gray-600 mb-4'>Provide your spouse's information.</p>
          <div className='mb-4'>
            <label className='block font-bold text-gray-700 mb-1'>Spouse First Name*</label>
            <input
              type='text'
              className='w-full px-4 py-2 border rounded-lg'
              value={spouseInfo.firstName}
              onChange={(e) => setSpouseInfo({ ...spouseInfo, firstName: e.target.value.trim() })}
              required
            />
          </div>
          <div className='mb-4'>
            <label className='block font-bold text-gray-700 mb-1'>Spouse Last Name*</label>
            <input
              type='text'
              className='w-full px-4 py-2 border rounded-lg'
              value={spouseInfo.lastName}
              onChange={(e) => setSpouseInfo({ ...spouseInfo, lastName: e.target.value.trim() })}
              required
            />
          </div>
          <div className='mb-4'>
            <label className='block font-bold text-gray-700 mb-1'>Spouse SSN*</label>
            <input
              ref={spouseSSNRef}
              type='text'
              className='w-full px-4 py-2 border rounded-lg'
              value={spouseInfo.ssn}
              onChange={(e) => setSpouseInfo({ ...spouseInfo, ssn: e.target.value.trim() })}
              required
            />
          </div>
          <div className='w-full flex flex-row items-center justify-between mt-4'>
            <button disabled={submittingInfo} onClick={handleBack} className={`px-4 py-2 text-white rounded-lg ${submittingInfo ? 'bg-gray-400 cursor-not-allowed' : 'bg-secondaryColor hover:bg-secondaryDarkColor duration-200'}`}>
              Back
            </button>
            <button disabled={!spouseInfo.firstName || !spouseInfo.lastName || !spouseInfo.ssn || spouseInfo.ssn.length !== 11 || submittingInfo} onClick={handleNext} className={`px-4 py-2 text-white rounded-lg ${!spouseInfo.firstName || !spouseInfo.lastName || !spouseInfo.ssn || spouseInfo.ssn.length !== 11 || submittingInfo ? 'bg-gray-400 cursor-not-allowed' : 'bg-primaryColor hover:bg-primaryDarkColor duration-200'}`}>
              Next
            </button>
          </div>
        </div>
      )}
      {step === 5 && (
        gettingEmbedURL ? (
          <div className='flex flex-row items-center justify-center gap-2'>
            <div className='loader ease-linear rounded-full border-[6px] border-t-[6px]  h-8 w-8 mt-4 mb-4'></div>
            <p>Generating Agreement...</p>
          </div>
        ) : (
          <div className='w-full h-full flex flex-1 flex-col items-center gap-4'>
            <div className='lg:w-1/2 w-11/12 h-[70vh]' id='iframe-container'></div>
            {submittingInfo && (
              <div className='flex flex-row items-center justify-center gap-2'>
                <div className='loader ease-linear rounded-full border-[6px] border-t-[6px]  h-8 w-8 mt-4 mb-4'></div>
                <p>Sending Information...</p>
              </div>
            )}
          </div>
        )
      )}
    </div>
  ) : (
    <div className='flex flex-row items-center justify-center gap-2'>
      <div className='loader ease-linear rounded-full border-[6px] border-t-[6px]  h-8 w-8 mt-4 mb-4'></div>
      <p>Loading...</p>
    </div>
  )
}

/**
 * 
 <div className='w-11/12 max-w-3xl flex justify-between mt-4'>
 {step > 1 && (
   <button onClick={handleBack} className='px-4 py-2 bg-gray-300 rounded-lg'>
     Back
   </button>
 )}
 {step < (customerData.customerDocuments.marriedFilingJoint ? 2 : 3) && (
   <button onClick={handleNext} className='px-4 py-2 bg-blue-500 text-white rounded-lg'>
     Next
   </button>
 )}
</div>
 */